<template>
  <div style="position: relative">
    <vs-avatar
      @click="$refs.update_avatar_input.click()"
      v-if="newProfilePictureUrl"
      class="profile-picture"
      size="110px"
      :src="
        constants.s3Bucket + '/editor/profile-pictures/' + newProfilePictureUrl + '?t=' + cacheBuster
      "
    />
    <vs-avatar
      @click="$refs.update_avatar_input.click()"
      v-else-if="profilePictureUrl"
      class="profile-picture"
      size="110px"
      :src="
        constants.s3Bucket + '/editor/profile-pictures/' + profilePictureUrl + '?t=' + cacheBuster
      "
    />
    <vs-avatar
      @click="$refs.update_avatar_input.click()"
      v-else
      class="profile-picture"
      size="100px"
      :src="require('@/assets/images/icons/gender/default.png')"
    />
    <vs-button
      radius
      color="primary"
      type="relief"
      icon="photo_camera"
      class="upload-btn vs-con-loading__container"
      @click="$refs.update_avatar_input.click()"
      ref="loadableButton"
      id="upload-profile-picture"
    ></vs-button>
    <vs-prompt
      title="Adjust Profile Picture"
      accept-text="Save"
      @cancel="closeEditProfilePicturePrompt"
      @accept="cropAndUploadProfilePicture"
      @close="closeEditProfilePicturePrompt"
      :active.sync="editProfilePicturePrompt"
    >
      <div class="con-exemple-prompt">
        <clipper-basic
          v-if="tempProfilePicture" 
          :src="tempProfilePicture"
          ref="clipper"
          :ratio="1"
          :init-width="100"
          :init-height="100"
        >
        </clipper-basic>
      </div>
    </vs-prompt>
    <input
      type="file"
      class="hidden"
      name="Profile Picture"
      ref="update_avatar_input"
      @change="openEditProfilePicturePrompt"
      accept="image/*"
      :disabled="disableInputField"
    />
  </div>
</template>

<script>
import {
  successNotifyService,
  failureNotifyService,
} from "@/services/errors.service.js";
import constants from "@/constants";

export default {
  props: ["profilePictureUrl"],
  data() {
    return {
      editProfilePicturePrompt: false,
      tempProfilePicture: null,
      constants,
      selectedFileName: "", // FIX: Store just the string name, not the DOM element
      newProfilePictureUrl: null,
      cacheBuster: Date.now(),
    };
  },
  computed: {
    otpSent() {
      return this.$store.state.auth.otpSent;
    },
    saveInProgress() {
      return this.$store.state.editor.saveInProgress;
    },
    isUserLoggedIn() {
      return this.$store.state.auth.isUserLoggedIn;
    },
    usersData() {
      return this.$store.state.users.data;
    },
    userId() {
      return this.$store.state.auth.user._id;
    },
    internetConnectionStatus() {
      return this.$store.state.users.internetConnectionStatus;
    },
    disableInputField() {
      return !this.isUserLoggedIn || !this.internetConnectionStatus;
    },
    organizationId() {
      return this.$store.state.organization.orgData._id;
    },
  },
  watch: {
    editProfilePicturePrompt: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (!newVal) {
          setTimeout(() => {
            const fileInput = this.$refs.update_avatar_input;
            if (fileInput) fileInput.value = "";
          }, 50);
        }
      },
    },
  },
  methods: {
    closeEditProfilePicturePrompt() {
      this.editProfilePicturePrompt = false;
    },
    profilePictureValidations(imgInput, maxSize) {
      if (imgInput.target.files && imgInput.target.files[0]) {
        let currentThis = this;
        const file = imgInput.target.files[0];

        if (imgInput.target.files.length > 1) {
          return false;
        }

        // UPDATED: Allow ALL image formats dynamically instead of hardcoding extensions
        if (!file.type.startsWith("image/")) {
          failureNotifyService({
            title: "Incorrect File Type",
            text: "Please upload a valid image file.",
            currentThis,
          });
          return false;
        }

        // UPDATED: Check against the new maxSize (5MB)
        if (file.size / (1024 * 1024) > maxSize) {
          failureNotifyService({
            title: "Image File Size Exceeded",
            text: `The size of the profile picture should be less than ${maxSize}MB.`,
            currentThis,
          });
          return false;
        }

        return true;
      }
      return false;
    },
    // Replace these methods inside UserProfilePicture.vue

    openEditProfilePicturePrompt(input) {
      if (!this.profilePictureValidations(input, 5)) {
        input.target.value = ''; // Reset if invalid
        return;
      }

      this.selectedFileName = input.target.files[0].name;

      const reader = new FileReader();
      reader.onload = (e) => {
        // 1. Open the modal first
        this.editProfilePicturePrompt = true;
        
        // 2. Wait for Vuesax modal animation to finish before rendering the image
        // This fixes the "Cannot read properties of undefined (reading 'clientWidth')" error
        setTimeout(() => {
          this.tempProfilePicture = e.target.result;
        }, 300);
      };
      reader.readAsDataURL(input.target.files[0]);
    },

    cropAndUploadProfilePicture() {
      const currentThis = this;
      
      if (!this.$refs.clipper) return; // Safety check
      
      // Capture the cropped canvas
      const canvas = this.$refs.clipper.clip();
      
      setTimeout(() => {
        // FIX FOR 413 ERROR: Compress the output image!
        // We output as 'image/jpeg' with 0.7 (70%) quality. 
        // This easily shrinks a 5MB input down to < 500kb for the server payload.
        canvas.toBlob(function (blob) {
          const reader = new FileReader();
          reader.onload = (e) => {
            // Send the compressed lightweight image to API service
            currentThis.updateImageService(e.target.result);
          };
          reader.readAsDataURL(blob);
        }, 'image/jpeg', 0.7); 
      }, 200); 
    },
    cropAndUploadProfilePicture() {
      const currentThis = this;
      
      if (!this.$refs.clipper) return; 
      
      // 1. Capture the original cropped canvas from the clipper
      const originalCanvas = this.$refs.clipper.clip();
      
      setTimeout(() => {
        // 2. Set our maximum allowed resolution (400x400 pixels is plenty for a profile pic)
        const MAX_SIZE = 400; 
        let width = originalCanvas.width;
        let height = originalCanvas.height;
        
        // 3. Calculate new dimensions, scaling down if the crop is larger than MAX_SIZE
        if (width > MAX_SIZE) {
          height = Math.round(height * (MAX_SIZE / width));
          width = MAX_SIZE;
        }

        // 4. Create a new, smaller canvas in the background
        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = width;
        resizedCanvas.height = height;
        const ctx = resizedCanvas.getContext("2d");
        
        // 5. Draw the large cropped image scaled down onto our tiny canvas
        ctx.drawImage(originalCanvas, 0, 0, width, height);

        // 6. Compress this new, tiny canvas to JPEG (70% quality)
        resizedCanvas.toBlob(function (blob) {
          const reader = new FileReader();
          reader.onload = (e) => {
            // Send the tiny, compressed image to the backend API
            // This will easily bypass the Nginx 1MB limit!
            currentThis.updateImageService(e.target.result);
          };
          reader.readAsDataURL(blob);
        }, 'image/jpeg', 0.7); 
      }, 200); 
    },
    updateImageService(finalImgData) {
      const _id = this.userId;

      this.$store.commit("editor/TOGGLE_SAVE_IN_PROGRESS", true);

      this.$vs.loading({
        container: "#upload-profile-picture",
        color: "primary",
        background: "white",
        scale: 0.45,
      });

      const profilePictureData = finalImgData;
      // FIX: Use the safely stored string here
      const profilePictureUrl = _id + "-" + this.selectedFileName;

      const ppObj = {
        profilePictureData,
        profilePictureUrl,
      };

      this.$store
        .dispatch("users/updateProfilePicture", { ppObj, _id })
        .then((res) => {
          this.newProfilePictureUrl = res.data.profilePictureUrl;
          this.cacheBuster = Date.now(); // Forces image update in UI
          
          successNotifyService({
             title: "Success",
             text: "Profile picture updated successfully.",
             currentThis: this
          });
        })
        .catch((err) => {
          console.error("Upload failed:", err);
          failureNotifyService({
             title: "Error",
             text: "Failed to upload the image.",
             currentThis: this
          });
        })
        .finally(() => {
          this.$store.commit("editor/TOGGLE_SAVE_IN_PROGRESS", false);
          this.$vs.loading.close("#upload-profile-picture > .con-vs-loading");
        });
    },
  },
};
</script>

<style scoped>
.profile-picture {
  width: 8rem;
  height: 8rem;
}

.profile-picture >>> .vs-avatar--con-img {
  border-radius: 5% !important;
}

.upload-btn {
  position: absolute;
  margin: auto;
  background: rgba(255, 255, 255, 0.35) !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.js-clipper-basic >>> .js-zoom-area {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>